import { useMemo, useState } from "react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { boilerBrands } from "../features/boilers/boilerErrorCodes.data";
import useSeo from "../hooks/useSeo";

export default function BoilerErrorCodesPage() {
  const [activeBrand, setActiveBrand] = useState("all");
  const [query, setQuery] = useState("");
  useSeo({
    title: "ქვაბის Error კოდები | Thermo Fix",
    description:
      "ცენტრალური გათბობის ქვაბების ყველაზე გავრცელებული Error კოდების განმარტებები: Ariston, Baxi, Vaillant, Bosch, Navien და სხვა ბრენდები.",
    path: "/boiler-error-codes",
    keywords:
      "boiler error codes georgia, Ariston error codes, Baxi error codes, Vaillant შეცდომები, ქვაბის კოდების ახსნა",
    image: "/boiler.png",
  });

  const normalizedQuery = query.trim().toLowerCase();

  const filteredBrands = useMemo(() => {
    return boilerBrands
      .filter((brand) => activeBrand === "all" || brand.id === activeBrand)
      .map((brand) => ({
        ...brand,
        codes: brand.codes.filter((item) => {
          if (!normalizedQuery) return true;
          return (
            item.code.toLowerCase().includes(normalizedQuery) ||
            item.issue.toLowerCase().includes(normalizedQuery) ||
            item.meaning.toLowerCase().includes(normalizedQuery) ||
            brand.name.toLowerCase().includes(normalizedQuery)
          );
        }),
      }))
      .filter((brand) => brand.codes.length > 0);
  }, [activeBrand, normalizedQuery]);

  const getBrandBadge = (name) => {
    return name
      .split(/[\s/]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  return (
    <div className="relative overflow-hidden">
      <Header />

      <section className="mx-auto w-full max-w-6xl px-6 pb-10 pt-14">
        <div className="glass-panel p-7 md:p-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5a82bb] dark:text-[#89abe0]">
            ქვაბის მხარდაჭერა
          </p>
          <h2 className="mt-3 text-3xl font-bold text-[#0e2748] dark:text-[#e5efff] md:text-5xl">
            ცენტრალური გათბობის ქვაბების Error კოდები
          </h2>
          <p className="mt-4 max-w-3xl text-[#4f6788] dark:text-[#b5c9e8]">
            აირჩიე ბრენდი ან მოძებნე კონკრეტული კოდი. აქ თავმოყრილია ყველაზე
            გავრცელებული შეცდომები და მარტივი განმარტებები.
          </p>

          <div className="mt-7 grid gap-3 md:grid-cols-[1fr_260px]">
            <input
              type="text"
              placeholder="მაგ: F.28, E01, ignition..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full rounded-2xl border border-[#d5e3f6] bg-white px-4 py-3 text-sm text-[#15375f] outline-none transition focus:border-[#7fa5d8] focus:ring-2 focus:ring-[#d4e5ff] dark:border-[#365073] dark:bg-[#15263d] dark:text-[#d5e6ff]"
            />
            <select
              value={activeBrand}
              onChange={(event) => setActiveBrand(event.target.value)}
              className="w-full rounded-2xl border border-[#d5e3f6] bg-white px-4 py-3 text-sm font-semibold text-[#15375f] outline-none transition focus:border-[#7fa5d8] focus:ring-2 focus:ring-[#d4e5ff] dark:border-[#365073] dark:bg-[#15263d] dark:text-[#d5e6ff]"
            >
              <option value="all">ყველა ბრენდი</option>
              {boilerBrands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {boilerBrands.map((brand) => (
              <button
                key={brand.id}
                type="button"
                onClick={() => setActiveBrand(brand.id)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition ${
                  activeBrand === brand.id
                    ? "border-[#0f3d7a] bg-[#0f3d7a] text-white dark:border-[#3b5f92] dark:bg-[#2b4f82]"
                    : "border-[#d5e3f6] bg-white text-[#214a7d] hover:border-[#9ebce3] dark:border-[#365073] dark:bg-[#15263d] dark:text-[#c6dcfa]"
                }`}
              >
                <span className="grid h-4 w-4 place-content-center rounded-full bg-white/90 text-[9px] font-bold text-[#163b6c] dark:bg-[#2a4263] dark:text-[#d5e6ff]">
                  {getBrandBadge(brand.name)}
                </span>
                {brand.name}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setActiveBrand("all")}
              className={`inline-flex items-center rounded-full border px-3 py-2 text-xs font-semibold transition ${
                activeBrand === "all"
                  ? "border-[#0f3d7a] bg-[#0f3d7a] text-white dark:border-[#3b5f92] dark:bg-[#2b4f82]"
                  : "border-[#d5e3f6] bg-white text-[#214a7d] hover:border-[#9ebce3] dark:border-[#365073] dark:bg-[#15263d] dark:text-[#c6dcfa]"
              }`}
            >
              ყველა ბრენდი
            </button>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-6 pb-16">
        {filteredBrands.length === 0 ? (
          <div className="rounded-2xl border border-[#dbe7f7] bg-white p-8 text-center text-[#4c668a] dark:border-[#365073] dark:bg-[#15263d] dark:text-[#c6dcfa]">
            მოცემული ძებნით მონაცემი ვერ მოიძებნა.
          </div>
        ) : (
          <div className="space-y-5">
            {filteredBrands.map((brand, index) => (
              <details
                key={brand.id}
                className="premium-card animate-rise group p-0"
                open={index === 0}
              >
                <summary className="cursor-pointer list-none rounded-2xl px-6 py-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="grid h-8 w-8 place-content-center rounded-lg border border-[#dbe7f8] bg-[#f5f9ff] text-xs font-bold text-[#163b6c] dark:border-[#365073] dark:bg-[#1e3655] dark:text-[#d5e6ff]">
                        {getBrandBadge(brand.name)}
                      </span>
                      <h3 className="text-xl font-bold text-[#0f294c] dark:text-[#e5efff]">{brand.name}</h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="rounded-full bg-[#e8f1ff] px-3 py-1 text-xs font-semibold text-[#215089] dark:bg-[#253f61] dark:text-[#c6dcfa]">
                        {brand.codes.length} კოდი
                      </span>
                      <img
                        src={brand.image}
                        alt={`${brand.name} boiler`}
                        className="hidden h-14 w-24 rounded-lg border border-[#dbe7f8] object-cover md:block"
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  </div>
                </summary>

                <div className="border-t border-[#e2ecfb] px-4 pb-4 pt-3 dark:border-[#365073] md:px-6 md:pb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] border-separate border-spacing-y-2">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-[0.12em] text-[#5a7eaf] dark:text-[#9dbbe7]">
                          <th className="px-3 py-2">კოდი</th>
                          <th className="px-3 py-2">შეცდომა</th>
                          <th className="px-3 py-2">განმარტება</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brand.codes.map((item) => (
                          <tr key={`${brand.id}-${item.code}`} className="bg-[#f7faff] text-sm text-[#29486f] dark:bg-[#182a42] dark:text-[#c8ddfa]">
                            <td className="rounded-l-xl px-3 py-3 font-bold text-[#163d73] dark:text-[#dbeaff]">
                              {item.code}
                            </td>
                            <td className="px-3 py-3 font-semibold">{item.issue}</td>
                            <td className="rounded-r-xl px-3 py-3">{item.meaning}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
